import * as React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import type { CmlContent } from 'bundles/cml';
import { CMLUtils } from 'bundles/cml';
import { TrackedButton } from 'bundles/common/components/TrackedCui';
import { getFluxiblePostId } from 'bundles/discussions/actions/ThreadDetailsActions';
import ForumsCmlEditor from 'bundles/discussions/components/ForumsCmlEditor';
import type { ForumPostWithCreator } from 'bundles/discussions/components/forumsV2-ForumThreads/__providers__/ForumPostDataProvider/__types__';
import { DTD_NAME, savingStates } from 'bundles/discussions/constants';
import DiscussionsCMLUtils from 'bundles/discussions/utils/DiscussionsCMLUtils';
import generateReplyData from 'bundles/discussions/utils/generateReplyData';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type GroupForumsV1 from 'bundles/naptimejs/resources/groupForums.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandCourseForumsV1 from 'bundles/naptimejs/resources/onDemandCourseForums.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandMentorForumsV1 from 'bundles/naptimejs/resources/onDemandMentorForums.v1';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/ReplyCMLInput';

type PropsFromCaller = {
  parentPost: ForumPostWithCreator;
  savingStateFromCaller: string;
  shouldFocusReplyEditor: boolean;
  placeholder: string;
  ariaDescribedBy: string;
  currentForum: OnDemandCourseForumsV1 | OnDemandMentorForumsV1 | GroupForumsV1;
  forumType: string;
  courseId: string;
  contextId: string;
  question: Record<string, any>; // TODO: make it more specific
  handleSubmit: (prop: Record<string, any>) => {}; // TODO: make it more specific
  defaultValue?: string;
  onSubmit(): void;
};

type PropsFromStore = {
  savingStateFromStore: string;
};

type Props = PropsFromCaller & PropsFromStore;

type disabledPlugin = { id: number; run: any };

type State = {
  cml: CmlContent;
  hasErrors: boolean;
  submitEnabled: boolean;
  disabledPlugins: Array<disabledPlugin>;
  editorKey: number;
  isFollowing: boolean;
  saved: boolean;
  saveError: boolean;
  saveErrorMessage: string;
};

class ReplyCMLInput extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    router: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof ReplyCMLInput.contextTypes>;

  static defaultProps = {
    placeholder: _t('Add details for others to answer your question'),
    savingStateFromCaller: savingStates.UNCHANGED,
    defaultValue: '',
  };

  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);
    this.state = this.getDefaultState();
  }

  componentWillReceiveProps(nextProps: $TSFixMe) {
    // when the saving state changes from either the caller or the store, we need to update the state of the component
    // in order to display the correct error if there is one present
    if (
      nextProps.savingStateFromCaller !== this.props.savingStateFromCaller ||
      nextProps.savingStateFromStore !== this.props.savingStateFromStore
    ) {
      // if the saving state is SAVED, reset component state
      if (
        nextProps.savingStateFromCaller === savingStates.SAVED ||
        nextProps.savingStateFromStore === savingStates.SAVED
      ) {
        const prevState = this.state;
        this.setState(() =>
          Object.assign(this.getDefaultState(), {
            editorKey: prevState.editorKey + 1,
          })
        );
      }
    }
    // if there is an error in the saving state, display the correct error
    if (nextProps.savingStateFromStore === savingStates.ERROR) {
      this.handleSaveError();
    } else if (nextProps.savingStateFromStore === savingStates.INVALID_TERM_ERROR) {
      this.handleInvalidTermError();
    } else if (nextProps.savingStateFromStore === savingStates.RATE_LIMIT_ERROR) {
      this.handleRateLimitError();
    } else if (nextProps.savingStateFromStore === savingStates.USER_BLOCKED_ERROR) {
      this.handleBlockedUserError();
    } else if (nextProps.savingStateFromStore === savingStates.SAVED) {
      this.handleSaveSuccess();
    }

    if (nextProps.question && nextProps.question.isFollowing !== this.state.isFollowing) {
      this.setState({ isFollowing: nextProps.question.isFollowing });
    }
  }

  handleSaveError() {
    this.setState({
      saveError: true,
      saveErrorMessage: _t('Something went wrong. Please reload the page and try again.'),
      saved: false,
    });
  }

  handleInvalidTermError() {
    this.setState({
      saveError: true,
      saveErrorMessage: _t(
        'There is something wrong with your post. Please review and edit it or contact support for assistance.'
      ),
      saved: false,
    });
  }

  handleBlockedUserError() {
    this.setState({
      saveError: true,
      saveErrorMessage: _t(
        'You are currently blocked from creating forum posts. Please contact support for assistance.'
      ),
      saved: false,
    });
  }

  handleRateLimitError() {
    this.setState({
      saveError: true,
      saveErrorMessage: _t('You have created too many posts in a short time period. Please wait before trying again'),
      saved: false,
    });
  }

  handleSaveSuccess() {
    this.props.onSubmit();
    this.setState({ submitEnabled: false, saved: true });
  }

  getDefaultState() {
    // Always show the toolbar unless this is a comment.
    const disabledPlugins = [] as Array<disabledPlugin>;
    const { question } = this.props;
    return {
      cml: CMLUtils.create(this.props.defaultValue, DTD_NAME),
      hasErrors: false,
      submitEnabled: false,
      disabledPlugins,
      editorKey: 1,
      isFollowing: question && question.isFollowing,
      saved: false,
      saveErrorMessage: '',
      saveError: false,
      savingStateFromStore: savingStates.UNCHANGED,
    };
  }

  handleInputChange = (cml: $TSFixMe) => {
    const hasErrors = !DiscussionsCMLUtils.validateLength(cml);
    const submitEnabled = true;

    this.setState({ cml, hasErrors, submitEnabled });
  };

  handleSubmit = () => {
    const { cml, isFollowing } = this.state;
    const { parentPost, contextId, currentForum, question } = this.props;
    const forumType = (currentForum && currentForum.forumType.typeName) || this.props.forumType;
    const options = generateReplyData(parentPost, contextId, cml, forumType, isFollowing);

    this.props.handleSubmit({
      options,
      question,
      parentPost,
      forumType,
      isFollowing,
    });
  };

  handleFollow = (isFollowing: $TSFixMe) => {
    this.setState({ isFollowing });
  };

  render() {
    const { parentPost, courseId, placeholder, ariaDescribedBy } = this.props;
    const { cml, editorKey, submitEnabled, hasErrors, saveError, saveErrorMessage } = this.state;

    const contentLengthWarning = DiscussionsCMLUtils.generateContentLengthWarning(cml);

    const submitError = <div className="c-error-text c-form-error-message">{saveErrorMessage}</div>;

    const classes = classNames('rc-ForumsV2__ReplyCML', 'flex-1');
    const questionIdParts = stringKeyToTuple(parentPost.forumQuestionId || '');
    const questionId = questionIdParts[questionIdParts.length - 1];
    const imageUploadOptions = DiscussionsCMLUtils.getImageUploadOptions(courseId, questionId);

    return (
      <div className={classes}>
        <div className="editor-container">
          <ForumsCmlEditor
            cml={cml}
            key={editorKey}
            placeholder={placeholder}
            imageUploadOptions={imageUploadOptions}
            ariaLabel={_t('Post Reply')}
            ariaDescribedBy={ariaDescribedBy}
            onChange={this.handleInputChange}
            renderMode={{
              typeName: 'resizable',
              label: _t('Post Reply'),
            }}
          />
        </div>

        {!this.state.saved && !!contentLengthWarning && (
          <div className="horizontal-box align-items-right" style={{ marginBottom: 20 }}>
            {contentLengthWarning}
          </div>
        )}

        {saveError && (
          <div className="horizontal-box align-items-right" style={{ marginBottom: 20 }}>
            {submitError}
          </div>
        )}

        {!this.state.saved && (
          <div className="horizontal-box align-items-right align-items-vertical-center">
            <TrackedButton
              trackingName="thread_reply"
              size="sm"
              type="primary"
              onClick={this.handleSubmit}
              disabled={!submitEnabled || hasErrors}
            >
              {_t('Reply')}
            </TrackedButton>
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  connectToStores<any, any>(
    ['ThreadDetailsStore'],
    ({ ThreadDetailsStore }, { parentPost, contextId, cml, forumType, isFollowing }) => {
      const options = generateReplyData(parentPost, contextId, cml, forumType, isFollowing);
      const storeId = getFluxiblePostId(options, parentPost);
      return {
        savingStateFromStore: ThreadDetailsStore.savingStates[storeId],
      };
    }
  )
)(ReplyCMLInput);
